import { useEffect, useRef, useState } from 'react';

import { getAccessToken } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useQueryClient } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_URL_API?.replace(/\/$/, '') || 'http://localhost:3230';

const MAX_RETRIES = 5;
const RETRY_DELAYS = [1_000, 2_000, 4_000, 8_000, 30_000];

/**
 * Connecte un stream SSE sur /notifications/stream.
 * Invalide les queries notifications à chaque event reçu.
 * Retourne `fallbackPolling: true` si le SSE a échoué après MAX_RETRIES.
 */
export function useNotificationSSE(): { fallbackPolling: boolean } {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();
  const retriesRef = useRef(0);
  const [fallbackPolling, setFallbackPolling] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const token = getAccessToken();
    if (!token) return;

    const controller = new AbortController();
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    async function connect() {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/notifications/stream`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE HTTP ${response.status}`);
        }

        // Connexion réussie — reset retries
        retriesRef.current = 0;
        setFallbackPolling(false);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('event: notification')) {
              void queryClient.invalidateQueries({
                queryKey: queryKeys.notifications.count(),
              });
              void queryClient.invalidateQueries({
                queryKey: queryKeys.notifications.list(),
              });
            }
          }
        }

        // Stream terminé — reconnecter
        if (!cancelled) scheduleReconnect();
      } catch (err: unknown) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;

        retriesRef.current++;

        if (retriesRef.current >= MAX_RETRIES) {
          setFallbackPolling(true);
          return;
        }

        scheduleReconnect();
      }
    }

    function scheduleReconnect() {
      if (cancelled) return;
      const delay = RETRY_DELAYS[Math.min(retriesRef.current, RETRY_DELAYS.length - 1)];
      reconnectTimer = setTimeout(() => {
        if (!cancelled) void connect();
      }, delay);
    }

    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [isAuthenticated, queryClient]);

  return { fallbackPolling };
}
